<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { computed, type HTMLAttributes } from 'vue';
import Input from './Input.vue';

const props = defineProps<{
  modelValue?: string | number;
  value?: string | number;
  defaultValue?: string | number;
  min?: number;
  max?: number;
  step?: number | string;
  precision?: number; // 0 表示仅保留整数
  formatter?: (val: string | number) => string | number;
  class?: HTMLAttributes['class'];
  id?: HTMLAttributes['id'];
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number): void;
  (e: 'change', payload: string | number): void;
}>();

// bridge v-model for consumers that use it
const innerModel = useVModel(props, 'modelValue', emits, {
  passive: true,
  defaultValue: props.defaultValue,
});

const roundToPrecision = (val: number, precision?: number): number => {
  // 根据精度对数值进行四舍五入
  if (precision === undefined) return val;
  const p = Math.max(0, Math.floor(precision));
  const factor = Math.pow(10, p);
  return Math.round(val * factor) / factor;
};

const clampToRange = (val: number, min?: number, max?: number): number => {
  // 将数值限制在[min, max]区间内
  let out = val;
  if (typeof min === 'number' && out < min) out = min;
  if (typeof max === 'number' && out > max) out = max;
  return out;
};

const applyNumberRules = (raw: string | number): string | number => {
  // 将原始输入转换为符合精度与范围规则的值
  const text = String(raw ?? '').trim();
  if (text === '') return '';
  const parsed = Number(text);
  if (Number.isNaN(parsed)) return raw;
  const rounded = roundToPrecision(parsed, props.precision);
  const clamped = clampToRange(rounded, props.min, props.max);
  return props.precision === 0 ? String(Math.trunc(clamped)) : clamped;
};

const formatForDisplay = (val: string | number): string | number => {
  return props.formatter ? props.formatter(val) : val;
};

const onInputTransform = (raw: string): string | number => {
  // 允许自由输入（不打断输入过程），值在提交时再进行规范化
  return raw;
};

const onCommitTransform = (raw: string | number): string | number => {
  // 在提交（change/blur）时应用数值规则
  return applyNumberRules(raw);
};

const forwardedValue = computed(() => props.modelValue ?? props.value ?? props.defaultValue ?? '');
</script>

<template>
  <Input
    :type="'number'"
    :modelValue="forwardedValue"
    :class="class"
    :id="id"
    :inputTransform="onInputTransform"
    :commitTransform="onCommitTransform"
    :formatter="formatForDisplay"
    v-bind="$attrs"
    :min="min"
    :max="max"
    :step="step ?? 1"
    @update:modelValue="v => emits('update:modelValue', v)"
    @change="v => emits('change', v)"
  />
</template>
